import { useState, useRef } from 'react';
import { ArrowLeft, Upload, BookOpen, Trash2, Download, Loader2, MoreVertical, Sparkles, Inbox, ScrollText, Filter } from 'lucide-react';
import { Button } from '../ui/Button';
import { IconTooltip } from '../ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { CaseStatusBadge } from '../cases/CaseStatusBadge';
import { CaseSearchField } from './CaseSearchField';
import type { Case } from '../../types';

interface CaseTopBarProps {
  caseData: Case;
  onBack: () => void;
  onUploadDocuments: () => void;
  onOpenCaseDict?: () => void;
  // AI context-awareness panel (correlation risks + entities).
  onOpenRisks?: () => void;
  riskCount?: number;
  // The NextRequest request a request-sourced case came from (FB-1007 #8).
  onOpenSourceRequest?: () => void;
  onRenameCase?: (newName: string) => void;
  onDeleteCase?: () => void;
  /** Opens the export dialog (ZIP or single PDF, and which documents go in). */
  onExport?: () => void;
  isExporting?: boolean;
  /** "Search case" (FR-04): called with the query; shown left of Source request. */
  onSearchCase?: (query: string) => void;
  /** Case menu → "Export decision log" (R2-11): searches and review decisions. */
  onExportDecisionLog?: () => void;
  /** Case menu → "Build a Purview search" (R2-12): a KeyQL query from the request. */
  onBuildPurviewSearch?: () => void;
}

export function CaseTopBar({
  caseData,
  onBack,
  onUploadDocuments,
  onOpenCaseDict,
  onOpenRisks,
  riskCount = 0,
  onOpenSourceRequest,
  onRenameCase,
  onDeleteCase,
  onExport,
  isExporting,
  onSearchCase,
  onExportDecisionLog,
  onBuildPurviewSearch,
}: CaseTopBarProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(caseData.name);
  const [menuOpen, setMenuOpen] = useState(false);
  // Enter/Escape commit or cancel synchronously; the resulting blur must not
  // re-run the commit (which would double-fire or override a cancel).
  const skipBlurRef = useRef(false);

  const handleStartEdit = () => {
    skipBlurRef.current = false;
    setEditName(caseData.name);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditName(caseData.name);
    setIsEditing(false);
  };

  const handleSave = () => {
    const trimmed = editName.trim();
    if (trimmed && trimmed !== caseData.name) {
      onRenameCase?.(trimmed);
    }
    setIsEditing(false);
  };

  const handleBlur = () => {
    if (skipBlurRef.current) {
      skipBlurRef.current = false;
      return;
    }
    handleSave();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      skipBlurRef.current = true;
      handleSave();
    } else if (e.key === 'Escape') {
      skipBlurRef.current = true;
      handleCancel();
    }
  };

  return (
    <div className="relative h-14 border-b border-border bg-white flex items-center justify-between px-4">
      {/* Left Section */}
      <div className="flex items-center gap-4">
        <IconTooltip label="Back to cases">
          <button
            onClick={onBack}
            className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
            aria-label="Back to cases"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
        </IconTooltip>

        {/* Fixed-width name slot — keeps the title from pushing other header
            content around when it swaps to the edit input. */}
        <div className="w-[300px] flex items-center">
          {isEditing ? (
            // Edit mode: just an underline beneath the name — no box, no
            // save/cancel buttons. Enter or clicking away commits; Escape cancels.
            <input
              autoFocus
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              onFocus={(e) => e.target.select()}
              maxLength={500}
              aria-label="Case name"
              className="font-medium text-sm bg-transparent border-0 border-b-2 border-primary outline-none px-0 py-0.5 w-full"
            />
          ) : onRenameCase ? (
            // Click the name to edit it; on hover it gets a dashed underline.
            <IconTooltip label="Click to edit">
              <button
                onClick={handleStartEdit}
                aria-label={`Rename case: ${caseData.name}`}
                className="font-medium text-sm truncate max-w-full border-b-2 border-dashed border-transparent hover:border-gray-400 transition-colors"
              >
                {caseData.name}
              </button>
            </IconTooltip>
          ) : (
            <span className="font-medium text-sm truncate max-w-full" title={caseData.name}>{caseData.name}</span>
          )}
        </div>
      </div>

      {/* Status badge centered in the header, independent of the left/right
          content so it sits dead-center regardless of the title width. */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
        <CaseStatusBadge status={caseData.status} />
      </div>

      {/* Right Section */}
      <div className="flex items-center gap-1.5">
        {onSearchCase && <CaseSearchField onSearch={onSearchCase} />}
        {onOpenSourceRequest && (
          <Button
            variant="outline"
            size="sm"
            className="hover:bg-muted hover:text-foreground"
            onClick={onOpenSourceRequest}
          >
            <Inbox className="h-4 w-4 mr-1.5" />
            Source request
          </Button>
        )}
        {onOpenCaseDict && (
          <Button
            variant="outline"
            size="sm"
            className="hover:bg-muted hover:text-foreground"
            onClick={onOpenCaseDict}
          >
            <BookOpen className="h-4 w-4 mr-1.5" />
            Case Dict
          </Button>
        )}

        {onOpenRisks && (
          <IconTooltip label={riskCount > 0 ? `${riskCount} correlation risk${riskCount === 1 ? '' : 's'}` : 'Context awareness'}>
            <span className="relative inline-flex">
              <Button
                variant="outline"
                size="sm"
                className="hover:bg-muted hover:text-foreground"
                onClick={onOpenRisks}
                aria-label="Context awareness"
              >
                <Sparkles className="h-4 w-4 text-primary" />
              </Button>
              {riskCount > 0 && (
                <span className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-semibold text-white">
                  {riskCount > 99 ? '99+' : riskCount}
                </span>
              )}
            </span>
          </IconTooltip>
        )}

        {onOpenCaseDict && onExport && (
          <div className="h-5 w-px bg-border mx-0.5" />
        )}

        {onExport && (
          <IconTooltip label="Export the case as a ZIP or a single PDF">
            <Button
              variant="outline"
              size="sm"
              className="h-8 gap-1.5 hover:bg-muted hover:text-foreground"
              onClick={onExport}
              disabled={isExporting}
            >
              {isExporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Export
            </Button>
          </IconTooltip>
        )}

        {onExport && (
          <div className="h-5 w-px bg-border mx-0.5" />
        )}

        {/* Overflow menu: Upload, decision log, Delete (native title; nesting IconTooltip on PopoverTrigger breaks refs on React 18) */}
        <Popover open={menuOpen} onOpenChange={setMenuOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8 hover:bg-muted hover:text-foreground"
              aria-label="More actions"
              title="More actions"
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-56 p-1">
            <button
              onClick={() => { setMenuOpen(false); onUploadDocuments(); }}
              className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm text-foreground hover:bg-muted transition-colors"
            >
              <Upload className="h-3.5 w-3.5 text-gray-500" />
              Upload Documents
            </button>
            {onExportDecisionLog && (
              <button
                onClick={() => { setMenuOpen(false); onExportDecisionLog(); }}
                className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm text-foreground hover:bg-muted transition-colors"
              >
                <ScrollText className="h-3.5 w-3.5 text-gray-500" />
                Export decision log
              </button>
            )}
            {onBuildPurviewSearch && (
              <button
                onClick={() => { setMenuOpen(false); onBuildPurviewSearch(); }}
                className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm text-foreground hover:bg-muted transition-colors"
              >
                <Filter className="h-3.5 w-3.5 text-gray-500" />
                Build a Purview search
              </button>
            )}
            {onDeleteCase && (
              <button
                onClick={() => { setMenuOpen(false); onDeleteCase(); }}
                className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Delete All Files
              </button>
            )}
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
